<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemStorageLayers') }}</h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchStorageLayers"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <!-- ContentType filter field -->
            <x-select
                :id="`selectContentType`"
                :name="`selectContentType`"
                @selected="$_onSelectContentType"
                :options="contentTypeOptions"
                :textDefaultOption="$t('typeContentFilter')"
                required
                :value="selectedContentType"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <!-- Webtheme filter field -->
            <x-select
                :id="`selectWebtheme`"
                class="padding_left_small"
                :name="`selectWebtheme`"
                @selected="$_onSelectWebtheme"
                :options="webthemeOptions"
                :textDefaultOption="$t('typeWebthemeFilter')"
                :value="selectedWebtheme"
                :simpleMode="true"
                :saveInState="true"
            >
            </x-select>
            <b-link class="px-3 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilters') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :sort-by.sync="sortBy"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="fields"
                sort-icon-left
            >
                <template v-slot:cell(changeLogEnabled)="{ item }">
                    <!-- Button to open modal with table content -->
                    <b-button
                        v-if="item.changeLogEnabled"
                        size="sm"
                        variant="secondary"
                        @click="$_showLog(item)"
                    >
                        {{ $t('labelChangeLogShow') }}
                    </b-button>
                </template>
            </b-table>
        </div>
        <!-- No content for overview -->
        <div v-show="noThemesToshow" class="col-md-12">
            <span> &nbsp; {{ $t('feedbackNonePresent') }}</span>
        </div>
        <router-links-footer :routerLinks="[{ name: 'feedbackDetailNew' }]" />
        <feedback-log
            :launchModal="launchFeedbackLogModal"
            :feedbackType="feedbackType"
            @close="$_closeLog"
        />
    </b-container>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { CONTENT_TYPE_ID } from '../../constants';
    import SearchBox from '../../components/SearchBox.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import FeedbackLog from './FeedbackLog.vue';

    export default {
        name: 'FeedbackOverview',

        components: {
            'search-box': SearchBox,
            'x-select': XSelect,
            'router-links-footer': RouterLinksFooter,
            'feedback-log': FeedbackLog
        },

        data: function () {
            return {
                fields: [
                    {
                        key: 'alias',
                        label: this.$t('columnNameName'),
                        sortable: true,
                        tdClass: 'width-60'
                    },
                    {
                        key: 'contentTypeId',
                        label: this.$t('columnNameContentType'),
                        sortable: true,
                        formatter: (value) => {
                            if (value === CONTENT_TYPE_ID.FEEDBACK) {
                                return this.$t('feedback');
                            } else if (value === CONTENT_TYPE_ID.MEASUREMENT) {
                                return this.$t('labelMeasure');
                            } else {
                                return '-';
                            }
                        }
                    },
                    {
                        key: 'useInWebTheme',
                        label: this.$t('labelWebTheme'),
                        sortable: true,
                        tdClass: 'text-center width-10',
                        formatter: (value) => (value ? this.$t('btnTitleYes') : '')
                    },
                    {
                        key: 'changeLogEnabled',
                        tdClass: 'text-center width-10',
                        label: '',
                        thStyle: { border: 0, width: '60px' }
                    }
                ],
                sortBy: 'alias',
                records: [],
                searchText: '',
                noThemesToshow: false,
                selectedContentType: '',
                selectedWebtheme: '',
                contentTypeOptions: [
                    { value: CONTENT_TYPE_ID.FEEDBACK, text: this.$t('feedback') },
                    {
                        value: CONTENT_TYPE_ID.MEASUREMENT,
                        text: this.$t('labelMeasure')
                    }
                ],
                webthemeOptions: [
                    { value: true, text: this.$t('btnTitleYes') },
                    { value: false, text: this.$t('btnTitleNo') }
                ],
                launchFeedbackLogModal: false,
                feedbackType: null
            };
        },

        computed: {
            dataLoaded() {
                return this.records.length > 0;
            },

            filteredRecords() {
                let records = this.records;
                // filter records on NAME
                if (this.searchText.length > 0) {
                    records = records.filter((record) => {
                        return (
                            record.alias
                                .toLowerCase()
                                .includes(this.searchText.toLowerCase()) ||
                            record.name
                                .toLowerCase()
                                .includes(this.searchText.toLowerCase())
                        );
                    });
                }
                // filter records on content type
                if (this.selectedContentType && this.selectedContentType > 0) {
                    records = records.filter((record) => {
                        return record.contentTypeId === this.selectedContentType;
                    });
                }

                // filter records on webtheme
                if (this.selectedWebtheme === true) {
                    records = records.filter(
                        (record) => record.useInWebTheme === true
                    );
                } else if (this.selectedWebtheme === false) {
                    records = records.filter(
                        (record) => record.useInWebTheme === false
                    );
                }

                return records;
            }
        },

        mounted() {
            this.$_getFeedbacks();
        },

        methods: {
            async $_getFeedbacks() {
                nprogress.start();
                // get all feedback data from the backend
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetFeedbackTypeOverview'
                    });

                    if (result) {
                        this.noThemesToshow = result.data.length === 0;
                        this.records = Object.assign([], result.data);
                        nprogress.done();
                    }
                } catch (error) {
                    nprogress.done();
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            $_onDetails(record) {
                this.$router.push({
                    path: `/dashboard/feedback/detail/${record.id}`
                });
            },

            $_onInput(searchText) {
                this.searchText = searchText;
            },

            $_rowClass() {
                return 'tablerow';
            },

            $_onSelectContentType(input) {
                this.selectedContentType = input;
            },

            $_onSelectWebtheme(input) {
                this.selectedWebtheme = input;
            },

            $_clearFilters() {
                this.$_onInput('');
                this.$_onSelectContentType('');
                this.$_onSelectWebtheme('');
            },
            $_showLog(item) {
                this.launchFeedbackLogModal = true;
                this.feedbackType = item;
            },
            $_closeLog() {
                this.launchFeedbackLogModal = false;
            }
        }
    };
</script>
<style>
.width-60 {
    width: 90rem;
}
.width-10 {
    width: 10rem;
}
</style>
